import type { ChordBorrowing, ChordFunction } from './types.js'

interface DescribeInput {
  chordFunction: ChordFunction
  borrowing: ChordBorrowing
}

/**
 * Look up a one-line rationale (≤60 chars) for a (chordFunction, borrowing)
 * combination. These templates are owned by Bowie and reviewed by Joni.
 */
const TEMPLATES: Record<string, string> = {
  // Major-scale diatonic
  'I|null': 'Tonic — home.',
  'ii|null': 'Supertonic — predominant.',
  'iii|null': 'Mediant — passing color.',
  'IV|null': 'Subdominant — predominant motion.',
  'V|null': 'Dominant — resolves to tonic.',
  'vi|null': 'Submediant — relative-minor home.',
  'vii°|null': 'Leading tone — sharp pull to tonic.',

  // Minor-scale diatonic
  'i|null': 'Tonic — home.',
  'ii°|null': 'Supertonic — predominant.',
  '♭III|null': 'Mediant — relative-major home.',
  'iv|null': 'Subdominant — predominant motion.',
  'v|null': 'Dominant — weak pull (no leading tone).',
  '♭VI|null': 'Submediant — lifted color.',
  '♭VII|null': 'Subtonic — natural minor.',

  // Minor-flavored mode diatonic chords (Dorian / Phrygian / Mixolydian / Locrian).
  // Bowie-owned register: `<Role> — <context>.`
  'i°|null': 'Tonic diminished — Locrian center.',
  'iii°|null': 'Mediant diminished — Mixolydian color.',
  'v°|null': 'Dominant diminished — Phrygian color.',
  'vi°|null': 'Submediant diminished — Dorian color.',
  '♭iii|null': 'Mediant — Locrian.',
  '♭V|null': 'Major triad on flat-five.',
  '♭vii|null': 'Subtonic minor — Phrygian color.',

  // Lydian-distinctive diatonic chords (v1.5b).
  'II|null': 'Supertonic major — Lydian color.',
  '#iv°|null': 'Raised-fourth diminished — Lydian characteristic.',
  'vii|null': 'Leading-tone minor — Lydian.',

  // Harmonic-minor-distinctive diatonic chord (v1.5c).
  '♭III+|null': 'Augmented mediant — harmonic minor.',

  // Parallel-mode borrowings (chordFunction may also appear in minor-diatonic
  // above; the borrowing field disambiguates).
  '♭III|parallel-mode': 'Borrowed from parallel minor.',
  'iv|parallel-mode': 'Minor subdominant — borrowed from parallel minor.',
  '♭VI|parallel-mode': 'Borrowed from parallel minor.',
  '♭VII|parallel-mode': 'Borrowed from parallel minor — Mixolydian color.',
  '♭II|parallel-mode': 'Neapolitan — Phrygian color.',
  // (When borrowing into minor from parallel major)
  'IV|parallel-mode': 'Major subdominant — borrowed from parallel major.',

  // Secondary dominants
  'V/ii|secondary-dominant': 'Secondary dominant of ii.',
  'V/IV|secondary-dominant': 'Secondary dominant of IV.',
  'V/V|secondary-dominant': 'Secondary dominant of V.',
  'V/vi|secondary-dominant': 'Secondary dominant of vi.',

  // Tritone substitutions (jazz, v1.5e). Borrowing is null because subV is a
  // chord-function category in its own right — distinct from `secondary-
  // dominant` (which preserves the leading-tone pull) and from `parallel-
  // mode` (modal-interchange borrowing). Each label resolves to its target
  // by half-step descent, the same b6→5 / b2→1 motion the original V would
  // have given by perfect-fifth descent.
  'subV/I|null': 'Tritone sub for V — resolves to tonic.',
  'subV/ii|null': 'Tritone sub for V/ii — resolves to ii.',
  'subV/IV|null': 'Tritone sub for V/IV — resolves to IV.',
  'subV/V|null': 'Tritone sub for V/V — resolves to V.',
  'subV/vi|null': 'Tritone sub for V/vi — resolves to vi.',

  // Chromatic outliers
  'chromatic|null': 'Outside the key.',
}

/**
 * Jazz-style description overrides for parallel-mode borrowings. When the
 * analyzer is invoked with `style: 'jazz'` (`describeChord(input, { style:
 * 'jazz' })`), these descriptions replace the defaults above. Two cases for
 * v1.5e — minor subdominant (iv) and ♭VII — are the jazz workhorse
 * borrowings and benefit from idiomatic phrasing. Other parallel-mode entries
 * fall through to the defaults.
 */
const JAZZ_OVERRIDES: Record<string, string> = {
  'iv|parallel-mode': 'Minor subdominant — jazz-typical borrowing.',
  '♭VII|parallel-mode': 'Borrowed from Mixolydian — jazz/rock idiom.',
}

/**
 * Optional style hint for `describeChord`. Mirrors `AnalysisContext.style`.
 * Currently only `'jazz'` activates an override table; `'blues'` keeps the
 * defaults (the blues description-swap for I7 / IV7 lives in `analyzeChord`
 * because it's gated on dom7 *and* chord function, not just function).
 */
export type DescribeStyle = 'blues' | 'jazz'

export interface DescribeOptions {
  style?: DescribeStyle
}

export const describeChord = (
  { chordFunction, borrowing }: DescribeInput,
  options: DescribeOptions = {}
): string => {
  const key = `${chordFunction}|${borrowing ?? 'null'}`
  if (options.style === 'jazz') {
    const jazzDescription = JAZZ_OVERRIDES[key]
    if (jazzDescription) {
      return jazzDescription
    }
  }
  return TEMPLATES[key] ?? 'Outside the key.'
}
